import React, { useState } from 'react';
import {
  Badge, Button, ConfirmDialog, DataTable, Input, Modal, PageHead, Switch, Textarea, inr, num,
} from '../components/ui';
import useList from '../hooks/useList';
import { membershipApi } from '../api';
import { useToast } from '../context/ToastContext';

const PERIODS = [
  { value: 'free', label: 'Free (never renews)' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly', label: 'Yearly' },
];

const emptyForm = {
  code: '', name: '', description: '', price: 0, billingPeriod: 'monthly',
  productLimit: '', features: '', isDefault: false, isActive: true, sortOrder: 0,
};

/**
 * Seller membership tiers.
 *
 * The listing cap is the point of this screen: a free tier that limits how
 * many products a seller may have live is what makes the paid tiers worth
 * buying. Leave the cap blank for unlimited.
 */
const Memberships = () => {
  const toast = useToast();
  const list = useList(membershipApi.list, {});

  const [form, setForm] = useState(null);
  const [retire, setRetire] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.name.trim()) return toast.error('Give the plan a name');
    if (!form._id && !form.code.trim()) return toast.error('Give the plan a code');

    setBusy(true);
    try {
      const payload = {
        ...form,
        price: Number(form.price) || 0,
        sortOrder: Number(form.sortOrder) || 0,
        // Blank means unlimited — zero would mean "may list nothing".
        productLimit: form.productLimit === '' ? null : Number(form.productLimit),
        features: String(form.features || '')
          .split('\n')
          .map(f => f.trim())
          .filter(Boolean),
      };

      if (form._id) await membershipApi.update(form._id, payload);
      else await membershipApi.create(payload);

      toast.success(form._id ? 'Plan updated' : 'Plan added');
      setForm(null);
      list.reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
    return undefined;
  };

  const doRetire = async () => {
    setBusy(true);
    try {
      const res = await membershipApi.remove(retire._id);
      toast.success(res.message);
      setRetire(null);
      list.reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const openEdit = row =>
    setForm({
      ...row,
      productLimit: row.productLimit === null || row.productLimit === undefined ? '' : row.productLimit,
      features: (row.features || []).join('\n'),
    });

  const columns = [
    {
      key: 'name',
      label: 'Plan',
      render: row => (
        <div>
          <div style={{ fontWeight: 600 }}>
            {row.name} {row.isDefault && <Badge status="active" tone="brand" dot={false}>Default</Badge>}
          </div>
          <div className="text-sm text-muted">{row.description || row.code}</div>
        </div>
      ),
    },
    {
      key: 'price',
      label: 'Charge',
      align: 'right',
      render: row =>
        row.price > 0 ? (
          <span>
            {inr(row.price)}
            <span className="text-muted text-sm"> / {row.billingPeriod}</span>
          </span>
        ) : (
          <span className="text-muted">Free</span>
        ),
    },
    {
      key: 'productLimit',
      label: 'Product limit',
      align: 'center',
      render: row =>
        row.productLimit === null || row.productLimit === undefined ? (
          <Badge status="active" tone="brand" dot={false}>Unlimited</Badge>
        ) : (
          <b>{num(row.productLimit)}</b>
        ),
    },
    { key: 'sortOrder', label: 'Order', align: 'center' },
    {
      key: 'isActive',
      label: 'Status',
      render: row => <Badge status={row.isActive ? 'active' : 'draft'}>{row.isActive ? 'Offered' : 'Retired'}</Badge>,
    },
    {
      key: 'actions',
      label: '',
      align: 'right',
      render: row => (
        <div className="row-actions">
          <Button size="sm" variant="ghost" onClick={() => openEdit(row)}>Edit</Button>
          {row.isActive && !row.isDefault && (
            <Button size="sm" variant="danger" onClick={() => setRetire(row)}>Retire</Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHead
        title="Seller memberships"
        subtitle="What sellers pay, and how many products each tier lets them list."
        actions={<Button icon="＋" onClick={() => setForm({ ...emptyForm })}>New plan</Button>}
      />

      <div className="filters">
        <div className="grow" />
        <span className="text-muted text-sm">{list.meta ? `${num(list.meta.total)} plans` : ''}</span>
      </div>

      <DataTable columns={columns} rows={list.rows} loading={list.loading} meta={list.meta} onPage={list.setPage} />

      <Modal
        open={!!form}
        title={form?._id ? 'Edit plan' : 'New plan'}
        onClose={() => setForm(null)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setForm(null)}>Cancel</Button>
            <Button loading={busy} onClick={submit}>{form?._id ? 'Save changes' : 'Add plan'}</Button>
          </>
        }
      >
        {form && (
          <>
            <div className="grid-2">
              <Input
                label="Code"
                value={form.code}
                disabled={!!form._id}
                onChange={e => setForm({ ...form, code: e.target.value })}
                placeholder="starter"
                hint={form._id ? 'Fixed once sellers are on this plan.' : 'Lowercase, no spaces.'}
              />
              <Input
                label="Name"
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="Starter"
              />
            </div>

            <Textarea
              label="Description"
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              rows={2}
              placeholder="For growing sellers."
            />

            <div className="grid-2">
              <Input
                label="Charge (₹)"
                type="number"
                value={form.price}
                onChange={e => setForm({ ...form, price: e.target.value })}
                hint="0 for a free tier."
              />

              <div className="field">
                <label>Billing period</label>
                <select
                  className="select"
                  value={form.billingPeriod}
                  onChange={e => setForm({ ...form, billingPeriod: e.target.value })}
                >
                  {PERIODS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>

              <Input
                label="Product limit"
                type="number"
                min={0}
                value={form.productLimit}
                onChange={e => setForm({ ...form, productLimit: e.target.value })}
                hint="Live products a seller may have. Leave blank for unlimited."
              />
              <Input
                label="Sort order"
                type="number"
                value={form.sortOrder}
                onChange={e => setForm({ ...form, sortOrder: e.target.value })}
              />
            </div>

            <Textarea
              label="Features"
              value={form.features}
              onChange={e => setForm({ ...form, features: e.target.value })}
              rows={4}
              placeholder={'Up to 25 live products\nPriority support'}
              hint="One per line. Shown to sellers."
            />

            <div className="col" style={{ gap: 11 }}>
              <Switch
                checked={form.isDefault}
                onChange={v => setForm({ ...form, isDefault: v })}
                label="Default plan — sellers without a paid plan fall back to this"
              />
              <Switch
                checked={form.isActive}
                onChange={v => setForm({ ...form, isActive: v })}
                label="Offer this plan"
              />
            </div>
          </>
        )}
      </Modal>

      <ConfirmDialog
        open={!!retire}
        title="Retire this plan?"
        message={`"${retire?.name}" will stop being offered. Sellers on it fall back to the default tier.`}
        confirmLabel="Retire"
        loading={busy}
        onConfirm={doRetire}
        onClose={() => setRetire(null)}
      />
    </>
  );
};

export default Memberships;
